import type {
  InvestmentBoardEntryCompactDto,
  InvestmentBoardFundDto,
  InvestmentBoardTimerDto,
} from '@/api/pipelines/types';

import { type PipelineCard, PipelineRouteKey } from '../../common/types';
import { getPipelinePersonLabel, isPresent } from '../../common/utils/mapping';

const UNNAMED_FUND = 'Unnamed fund';
const UNNAMED_INVESTOR = 'Unnamed investor';

/**
 * The card's Fund line. A fund the API could not name says so rather than
 * showing its id — the board used to render bare UUIDs here, which read as real
 * fund names. Null only when the card has no fund at all, which is a lead.
 */
export const getInvestmentFundLabel = (fund: InvestmentBoardFundDto | null): string | null =>
  fund ? fund.name?.trim() || UNNAMED_FUND : null;

export const mapInvestmentEntryToPipelineCard = (
  entry: InvestmentBoardEntryCompactDto,
): PipelineCard => {
  const investorLabel = getPipelinePersonLabel(entry.investor);
  const introducerLabel = entry.introducer ? getPipelinePersonLabel(entry.introducer) : null;

  return {
    id: entry.id,
    pipeline: PipelineRouteKey.Investment,
    stage: entry.stage,
    active: entry.active,
    title: getInvestmentFundLabel(entry.fund) ?? investorLabel ?? UNNAMED_INVESTOR,
    subtitleLines: [investorLabel, introducerLabel ? `via ${introducerLabel}` : null].filter(
      isPresent,
    ),
    badges: [
      {
        key: 'type',
        label: entry.introductionType,
        tone: 'neutral',
        variant: 'muted',
      },
    ],
    indicators: [
      {
        key: 'followingFund',
        label: 'Following fund',
        icon: 'eye',
        active: entry.followingFund,
      },
      {
        key: 'inCommunication',
        label: 'In communication',
        icon: 'message',
        active: entry.inCommunication,
      },
    ],
    timer: mapInvestmentTimer(entry.timer),
    lastStageChangedAt: entry.lastStageChangedAt,
    entityRefs: {
      ...(entry.fund ? { fundId: entry.fund.id } : {}),
      investorUserId: entry.investor.id,
      ...(entry.introducer ? { introducerUserId: entry.introducer.id } : {}),
    },
  };
};

const mapInvestmentTimer = (timer: InvestmentBoardTimerDto | null): PipelineCard['timer'] => {
  if (!timer) return null;

  return {
    label: `${timer.daysRemaining}d`,
    tone: mapInvestmentTimerStatusToTone(timer.status),
    expiresAt: timer.expiresAt,
  };
};

export const mapInvestmentTimerStatusToTone = (
  status: InvestmentBoardTimerDto['status'],
): NonNullable<PipelineCard['timer']>['tone'] => {
  if (status === 'Red') return 'red';
  if (status === 'Yellow') return 'yellow';
  return 'green';
};
